import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { useQueryState } from 'nuqs';
import { FC, ReactNode, useEffect, useState } from 'react';

import { useIsomorphicLayoutEffect } from '~/hooks/useIsomorphicLayoutEffect';
import { usePartner } from '~/hooks/usePartner';
import { useStepContext } from '~/provider/StepProvider';
import { MarketplacePartner } from '~/types/marketplace';
import { parseAsMarketplaceChannel } from '~/utils/channels';
import { sendParentEvent } from '~/utils/premarketplace';

import { Logo } from '../logo';

import { ProgressBar } from './components/progress-bar';

// Only the financing-interest step shows the partner logo alongside the progress bar
const STEPS_WITH_LOGO = new Set(['financing-interest']);

interface DialogTitleProps {
  channel: string;
  partner?: MarketplacePartner;
  includeLogo: boolean;
}

const DialogTitle: FC<DialogTitleProps> = ({ channel, partner, includeLogo }) =>
  includeLogo ? (
    <>
      <Stack marginRight="xlarge">
        <Logo channel={channel} partner={partner} type="minimal" width={40} />
      </Stack>
      <ProgressBar />
    </>
  ) : (
    <ProgressBar />
  );

interface PremarketplaceDialogProps {
  children: ReactNode;
}

export const PremarketplaceDialog: FC<PremarketplaceDialogProps> = ({ children }) => {
  const { currentStepId } = useStepContext();
  const [channel] = useQueryState('channel', parseAsMarketplaceChannel.withDefault('brighte'));
  const partner = usePartner();
  const [showCloseButton, setShowCloseButton] = useState(false);

  /**
   * Server renders with isOpen=false so the Radix Dialog portal is absent from
   * the SSR HTML. useIsomorphicLayoutEffect fires synchronously on the client
   * before the first paint, opening the dialog without a visible flash.
   * On the server it falls back to useEffect (a no-op during SSR), suppressing
   * React's "useLayoutEffect does nothing on the server" warning.
   */
  const [isOpen, setIsOpen] = useState(false);
  useIsomorphicLayoutEffect(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    try {
      setShowCloseButton(window?.top?.location.origin !== window?.location.origin);
    } catch {
      setShowCloseButton(true);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !showCloseButton) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, showCloseButton]);

  const onToggle = () => {
    if (isOpen) {
      sendParentEvent('track', { stepName: currentStepId, action: 'closed' });
      if (showCloseButton) setIsOpen(false);
    } else {
      sendParentEvent('track', { stepName: currentStepId, action: 'opened' });
      setIsOpen(true);
    }
  };

  const includeLogo = STEPS_WITH_LOGO.has(currentStepId);

  return (
    <ContentDialog
      title={<DialogTitle channel={channel} partner={partner} includeLogo={includeLogo} />}
      description={currentStepId}
      isOpen={isOpen}
      onToggle={onToggle}
      size="large"
      padding={{ desktop: 'xxxlarge', mobile: 'small' }}
      showCloseButton={showCloseButton}
      scrollable
      css={css`
        position: fixed !important;
        display: block !important;
        inset: 0;
        & > :first-child {
          height: auto;
          max-width: 600px;
          max-height: none;
          margin: auto;
        }
      `}
    >
      {children}
      <Box
        gap="xxxlarge"
        alignSelf="center"
        display="flex"
        flexDirection="row"
        position="absolute"
        css={css({
          bottom: -35,
        })}
      >
        <Text tone="neutralInverted">
          <TextLink href="https://brighte.com.au/agreement" target="_blank" rel="noopener noreferrer">
            Terms
          </TextLink>
        </Text>
        <Text tone="neutralInverted">
          <TextLink href="https://brighte.com.au/privacy" target="_blank" rel="noopener noreferrer">
            Privacy policy
          </TextLink>
        </Text>
      </Box>
    </ContentDialog>
  );
};
